import axios from 'axios'
import { getResponseData, normalizeQueryError } from 'src/queries/network.utils'
import type {
  CardLimitsResponse,
  CardManagementActivityPageResponse,
  CardManagementBillingContactResponse,
  CardManagementCapabilitiesResponse,
  CardManagementCardDetailsResponse,
  CardManagementCompanyLineResponse,
  CardManagementCurrentCycleResponse,
  CardManagementGeofenceResponse,
  CardManagementLifecycleOperationResponse,
  CardManagementMccRestrictionsResponse,
  CardManagementPageInput,
  CardManagementPaymentChannelsResponse,
  CardManagementPaymentPageResponse,
  CardManagementPaymentSummaryResponse,
  CardManagementStatementDocument,
  CardManagementStatementItemPageResponse,
  CardManagementStatementItemResponse,
  CardManagementStatementPageResponse,
  CardManagementStatusChangeResponse,
  CardManagementStatusResponse,
  ChangeCardManagementGeofenceCountryInput,
  ChangeCardManagementGeofenceCountryResponse,
  CreateCardCancellationRequestInput,
  CreateCardFraudReportInput,
  CreateCardManagementStatusChangeInput,
  CreateCardReplacementRequestInput,
  CreatePermanentLimitChangeInput,
  DownloadCardManagementStatementDocumentInput,
  LimitChangeResponse,
  ReplaceCardManagementBillingContactInput,
  ReplaceCardManagementBillingContactResponse,
  ReplaceCardManagementMccRestrictionsInput,
  ReplaceCardManagementMccRestrictionsResponse,
  SearchCardManagementGeofenceCountriesInput,
  SearchCardManagementGeofenceCountriesResponse,
  SetPaymentChannelInput,
  SetPaymentChannelResponse,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA } from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import { getRequestConfig, RequestConfig } from 'src/queries/requestConfig'

const requireCreditCardId = (creditCardId: string, resource: string): void => {
  if (!creditCardId) {
    throw new Error(`Card Management ${resource} require a creditCardId`)
  }
}

const MINIMUM_IDEMPOTENCY_KEY_LENGTH = 16
const MAXIMUM_IDEMPOTENCY_KEY_LENGTH = 255
const INVALID_IDEMPOTENCY_KEY_CHARACTER = /[^\x21-\x7e]/

const requireIdempotencyKey = (
  idempotencyKey: string,
  resource: string,
): void => {
  if (idempotencyKey === undefined || idempotencyKey.length === 0) {
    throw new Error(`Card Management ${resource} require an idempotencyKey`)
  }
  if (
    idempotencyKey.length < MINIMUM_IDEMPOTENCY_KEY_LENGTH ||
    idempotencyKey.length > MAXIMUM_IDEMPOTENCY_KEY_LENGTH ||
    INVALID_IDEMPOTENCY_KEY_CHARACTER.test(idempotencyKey)
  ) {
    throw new Error(
      `Card Management ${resource} require an idempotencyKey with 16-255 visible ASCII characters`,
    )
  }
}

export const fetchCardManagementCapabilities = async (
  creditCardId: string,
): Promise<CardManagementCapabilitiesResponse> => {
  requireCreditCardId(creditCardId, 'capabilities')

  // Returned verbatim. Nothing here may add, drop, or rewrite a verdict: the
  // server is the only thing entitled to say what a card can do, and
  // `getCardDetailsAvailability` distinguishes "denied" from "never asked" on
  // the assumption that this response is untouched.
  return getResponseData(
    axios.get<CardManagementCapabilitiesResponse>(
      `/creditcards/${creditCardId}/cardmanagement/capabilities`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const fetchCardManagementCardDetails = async (
  creditCardId: string,
  signal?: AbortSignal,
): Promise<CardManagementCardDetailsResponse> => {
  requireCreditCardId(creditCardId, 'card details')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.get<CardManagementCardDetailsResponse>(
      // Provisional path awaiting EX-40580; isolated here so it is cheap to replace.
      `/creditcards/${creditCardId}/cardmanagement/card-details`,
      { ...requestConfig, signal },
    ),
  )
}

export const fetchCardManagementLimits = async (
  creditCardId: string,
): Promise<CardLimitsResponse> => {
  requireCreditCardId(creditCardId, 'limits')

  return getResponseData(
    axios.get<CardLimitsResponse>(
      `/creditcards/${creditCardId}/cardmanagement/limits`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const fetchCardManagementStatus = async (
  creditCardId: string,
): Promise<CardManagementStatusResponse> => {
  requireCreditCardId(creditCardId, 'status')

  return getResponseData(
    axios.get<CardManagementStatusResponse>(
      `/creditcards/${creditCardId}/cardmanagement/status`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const fetchCardManagementMccRestrictions = async (
  creditCardId: string,
): Promise<CardManagementMccRestrictionsResponse> => {
  requireCreditCardId(creditCardId, 'MCC restriction reads')

  return getResponseData(
    axios.get<CardManagementMccRestrictionsResponse>(
      `/creditcards/${creditCardId}/cardmanagement/mcc-restrictions`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const fetchCardManagementOperation = async (
  operationId: string,
): Promise<CardManagementLifecycleOperationResponse> => {
  if (!operationId) {
    throw new Error('Card Management operation reads require an operationId')
  }

  return getResponseData(
    axios.get<CardManagementLifecycleOperationResponse>(
      `/cardmanagement/operations/${operationId}`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

const createStatusChange = async (
  operation: 'freeze' | 'unfreeze',
  {
    creditCardId,
    request,
    idempotencyKey,
  }: CreateCardManagementStatusChangeInput,
): Promise<CardManagementStatusChangeResponse> => {
  requireCreditCardId(creditCardId, 'status changes')
  requireIdempotencyKey(idempotencyKey, 'status changes')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.post<CardManagementStatusChangeResponse>(
      `/creditcards/${creditCardId}/cardmanagement/${operation}`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}

export const freezePhysicalCard = (
  input: CreateCardManagementStatusChangeInput,
): Promise<CardManagementStatusChangeResponse> =>
  createStatusChange('freeze', input)

export const unfreezePhysicalCard = (
  input: CreateCardManagementStatusChangeInput,
): Promise<CardManagementStatusChangeResponse> =>
  createStatusChange('unfreeze', input)

export const createPermanentLimitChange = async ({
  creditCardId,
  request,
  idempotencyKey,
}: CreatePermanentLimitChangeInput): Promise<LimitChangeResponse> => {
  requireCreditCardId(creditCardId, 'limit changes')
  requireIdempotencyKey(idempotencyKey, 'limit changes')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.post<LimitChangeResponse>(
      `/creditcards/${creditCardId}/cardmanagement/limit-change-requests`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}

export const replaceCardManagementMccRestrictions = async ({
  creditCardId,
  request,
  idempotencyKey,
}: ReplaceCardManagementMccRestrictionsInput): Promise<ReplaceCardManagementMccRestrictionsResponse> => {
  requireCreditCardId(creditCardId, 'MCC restriction replacements')
  requireIdempotencyKey(idempotencyKey, 'MCC restriction replacements')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.put<ReplaceCardManagementMccRestrictionsResponse>(
      `/creditcards/${creditCardId}/cardmanagement/mcc-restrictions`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}

const createLifecycleRequest = async (
  resource: string,
  {
    creditCardId,
    request,
    idempotencyKey,
  }:
    | CreateCardReplacementRequestInput
    | CreateCardFraudReportInput
    | CreateCardCancellationRequestInput,
): Promise<CardManagementLifecycleOperationResponse> => {
  requireCreditCardId(creditCardId, 'lifecycle requests')
  requireIdempotencyKey(idempotencyKey, 'lifecycle requests')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.post<CardManagementLifecycleOperationResponse>(
      `/creditcards/${creditCardId}/cardmanagement/${resource}`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}

export const createCardReplacementRequest = (
  input: CreateCardReplacementRequestInput,
): Promise<CardManagementLifecycleOperationResponse> =>
  createLifecycleRequest('replacement-requests', input)

export const createCardFraudReport = (
  input: CreateCardFraudReportInput,
): Promise<CardManagementLifecycleOperationResponse> =>
  createLifecycleRequest('fraud-reports', input)

export const createCardCancellationRequest = (
  input: CreateCardCancellationRequestInput,
): Promise<CardManagementLifecycleOperationResponse> =>
  createLifecycleRequest('cancellation-requests', input)

/**
 * Cursor-paginated Card Management reads (Activity, Statements, Payments).
 *
 * `limit` and `before` are only sent when supplied, so the server's documented
 * defaults (50 for Activity, 20 for the rest) stay the single source of truth
 * rather than being restated on the client. `before` is opaque — passed straight
 * back from the previous page's `nextCursor` and never parsed or constructed.
 */
const pageParams = ({
  limit,
  before,
}: Pick<CardManagementPageInput, 'limit' | 'before'>): Record<
  string,
  unknown
> => ({
  ...(limit === undefined ? {} : { limit }),
  ...(before === undefined ? {} : { before }),
})

const pageRequestConfig = (
  page: Pick<CardManagementPageInput, 'limit' | 'before'>,
) => {
  const requestConfig = getRequestConfig(RequestConfig.Default)
  return { ...requestConfig, params: pageParams(page) }
}

/**
 * Rejects a response echoing a card other than the one requested.
 *
 * Every Card Management read echoes `creditCardId` precisely so a client can
 * do this, and it is enforced at the network boundary rather than in a
 * consumer for two reasons: rendering another card's statements, payments, or
 * audit history is a data disclosure rather than a glitch, and React Query
 * would otherwise cache the foreign payload under THIS card's query key, where
 * it outlives the bad response.
 *
 * Applied to every read, including ones with no current consumer — these are
 * exported, so the next caller inherits the guard rather than having to know
 * about it.
 */
const requireEchoedCard = <T extends { creditCardId: string }>(
  page: T,
  creditCardId: string,
  resource: string,
): T => {
  if (page.creditCardId !== creditCardId) {
    throw new Error(`Card Management ${resource} belongs to another card`)
  }

  return page
}

export const fetchCardManagementActivity = async ({
  creditCardId,
  limit,
  before,
}: CardManagementPageInput): Promise<CardManagementActivityPageResponse> => {
  requireCreditCardId(creditCardId, 'activity reads')

  const page = await getResponseData(
    axios.get<CardManagementActivityPageResponse>(
      `/creditcards/${creditCardId}/cardmanagement/activity`,
      pageRequestConfig({ limit, before }),
    ),
  )

  return requireEchoedCard(page, creditCardId, 'activity page')
}

export const fetchCardManagementStatements = async ({
  creditCardId,
  limit,
  before,
}: CardManagementPageInput): Promise<CardManagementStatementPageResponse> => {
  requireCreditCardId(creditCardId, 'statement reads')

  const page = await getResponseData(
    axios.get<CardManagementStatementPageResponse>(
      `/creditcards/${creditCardId}/cardmanagement/statements`,
      pageRequestConfig({ limit, before }),
    ),
  )

  return requireEchoedCard(page, creditCardId, 'statement page')
}

export const fetchCardManagementStatementItems = async ({
  creditCardId,
  statementId,
  limit,
  before,
}: CardManagementPageInput & {
  statementId: string
}): Promise<CardManagementStatementItemPageResponse> => {
  requireCreditCardId(creditCardId, 'statement item reads')
  if (!statementId) {
    throw new Error(
      'Card Management statement item reads require a statementId',
    )
  }

  const page = await getResponseData(
    axios.get<CardManagementStatementItemPageResponse>(
      `/creditcards/${creditCardId}/cardmanagement/statements/${statementId}/items`,
      pageRequestConfig({ limit, before }),
    ),
  )

  requireEchoedCard(page, creditCardId, 'statement line-item page')
  // Line items echo the statement too, and that half matters just as much: a
  // page from another statement on the SAME card would pass the card check and
  // still put the wrong charges in this statement's ledger.
  if (page.statementId !== statementId) {
    throw new Error(
      'Card Management statement line-item page belongs to another statement',
    )
  }

  return page
}

/**
 * How many line-item pages one ledger read will walk before giving up.
 *
 * A bound rather than an unbounded `while`: `nextCursor` is server-controlled,
 * and a paging bug on either side would otherwise spin requests until the tab
 * is closed. 10 pages at the contract's 100-row maximum is 1,000 line items —
 * far beyond any real statement, so the cap should never be reached in
 * practice, and `isComplete: false` tells the caller when it was.
 */
const MAX_STATEMENT_ITEM_PAGES = 10

/** The contract's maximum page size, so the walk takes as few round trips as possible. */
const STATEMENT_ITEM_PAGE_LIMIT = 100

/**
 * The COMPLETE ledger for one statement, walking the cursor to the end.
 *
 * Reading only the first page is not sufficient here, and the failure is
 * silent: the statement-detail modal renders a "Total charges" row, so a
 * truncated ledger produces a total that disagrees with the statement it
 * belongs to rather than an obviously missing row. The contract's default page
 * size is 20 and a busy card's statement can exceed that easily.
 *
 * `nextCursor: null` is the SOLE terminal signal — the contract is explicit
 * that a final page may still contain items, so the walk keys on the cursor
 * and never on an empty `items`.
 *
 * Every continuation reads the same logical `snapshotAt` the first page
 * established, so late transactions and adjustments cannot shift or duplicate
 * rows mid-traversal.
 */
export const fetchAllCardManagementStatementItems = async ({
  creditCardId,
  statementId,
}: {
  creditCardId: string
  statementId: string
}): Promise<{
  creditCardId: string
  statementId: string
  items: CardManagementStatementItemResponse[]
  /** False when the page cap was hit before the server ran out of pages. */
  isComplete: boolean
}> => {
  const items: CardManagementStatementItemResponse[] = []
  let before: string | undefined

  for (
    let pageCount = 0;
    pageCount < MAX_STATEMENT_ITEM_PAGES;
    pageCount += 1
  ) {
    const page = await fetchCardManagementStatementItems({
      creditCardId,
      statementId,
      limit: STATEMENT_ITEM_PAGE_LIMIT,
      before,
    })

    // No echo check here: `fetchCardManagementStatementItems` already rejects a
    // page belonging to another card or statement, so every page appended
    // below has been validated. Re-checking would only let the two drift.
    items.push(...page.items)

    if (page.nextCursor === null) {
      return { creditCardId, statementId, items, isComplete: true }
    }

    before = page.nextCursor
  }

  return { creditCardId, statementId, items, isComplete: false }
}

/**
 * `Content-Disposition: attachment; filename="statement-{id}.pdf"` → the file
 * name.
 *
 * Deliberately narrow: only a quoted ASCII `filename` is honoured, and anything
 * with a path separator is rejected in favour of the caller's fallback. The
 * value is written straight into an `<a download>` attribute, so a server (or
 * anything able to influence that header) must not be able to steer where the
 * browser writes. RFC 5987 `filename*` is not parsed because the contract does
 * not emit it.
 */
const parseAttachmentFileName = (
  contentDisposition: string | undefined,
): string | undefined => {
  const match = /filename="([^"]+)"/.exec(contentDisposition ?? '')
  const fileName = match?.[1]

  return fileName && !/[/\\]/.test(fileName) ? fileName : undefined
}

/**
 * Reads a `Blob` as text.
 *
 * `Blob.text()` where it exists, `FileReader` otherwise — the fallback is not
 * theoretical: Safari only gained `text()` in 14, and jsdom still has no
 * implementation, so without it every error body below would be unreadable in
 * the test environment and on older browsers alike.
 */
const readBlobText = (blob: Blob): Promise<string> =>
  typeof blob.text === 'function'
    ? blob.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result))
        reader.onerror = () => reject(reader.error)
        reader.readAsText(blob)
      })

/**
 * Recovers the Card Management error payload from a FAILED blob request.
 *
 * With `responseType: 'blob'` axios hands back the error body as a `Blob` too,
 * which `normalizeQueryError` cannot read — every failure would otherwise
 * flatten to a bare status line and the caller would lose `CARD-MGMT.26`
 * (format unavailable) versus `CARD-MGMT.27` (temporarily unavailable), which
 * are the two the UI has to tell apart.
 *
 * Detected by DUCK-TYPING the response rather than via `axios.isAxiosError`,
 * matching `normalizeQueryError`'s own fallback: a blob body is the only thing
 * that matters here, and the type guard is the kind of thing an interceptor or
 * a re-thrown error can lose on the way.
 */
const readBlobErrorPayload = async (error: unknown): Promise<unknown> => {
  if (typeof error !== 'object' || error === null) {
    return error
  }

  const { response } = error as { response?: { data?: unknown } }

  if (!(response?.data instanceof Blob)) {
    return error
  }

  try {
    const text = await readBlobText(response.data)
    return {
      ...error,
      // Carried explicitly: `message` and `stack` are own but NON-ENUMERABLE on
      // an `Error`, so the spread above silently drops both. Without this, a
      // body carrying neither `error` nor `code` normalizes to
      // `{ error: undefined }` and there is nothing left to show or log.
      message: (error as { message?: string }).message,
      response: { ...response, data: JSON.parse(text) },
    }
  } catch {
    // A non-JSON body (an HTML gateway page, a truncated response) is not a
    // contract error — fall back to the raw failure rather than inventing one.
    return error
  }
}

/**
 * Downloads one statement document (EX-40602).
 *
 * `GET /creditcards/{id}/cardmanagement/statements/{statementId}/documents/{format}`
 * answers with BYTES, not a url: the contract publishes no signed url, storage
 * path, or bucket, so the document is fetched over the authenticated session
 * and delivered from memory by the caller.
 *
 * `Accept` is overridden per format because the shared config asks for the
 * Extend JSON vendor type, which this route does not produce.
 */
export const downloadCardManagementStatementDocument = async ({
  creditCardId,
  statementId,
  format,
}: DownloadCardManagementStatementDocumentInput): Promise<CardManagementStatementDocument> => {
  requireCreditCardId(creditCardId, 'statement document reads')
  if (!statementId) {
    throw new Error(
      'Card Management statement document reads require a statementId',
    )
  }

  const { mediaType } = CARD_MANAGEMENT_STATEMENT_DOCUMENT_MEDIA[format]
  const requestConfig = getRequestConfig(RequestConfig.Default)

  try {
    const response = await axios.get<Blob>(
      `/creditcards/${creditCardId}/cardmanagement/statements/${statementId}/documents/${format}`,
      {
        ...requestConfig,
        headers: { ...requestConfig.headers, Accept: mediaType },
        responseType: 'blob',
      },
    )

    return {
      format,
      blob: response.data,
      // Usually `null` — see `CardManagementStatementDocument.fileName`. Read
      // opportunistically so the server's name is honoured if the header is
      // ever added to the API's CORS exposed-headers list, but the caller is
      // what actually names the file.
      fileName:
        parseAttachmentFileName(response.headers['content-disposition']) ??
        null,
    }
  } catch (error) {
    throw normalizeQueryError(await readBlobErrorPayload(error))
  }
}

export const fetchCardManagementPaymentSummary = async (
  creditCardId: string,
): Promise<CardManagementPaymentSummaryResponse> => {
  requireCreditCardId(creditCardId, 'payment summary reads')

  const summary = await getResponseData(
    axios.get<CardManagementPaymentSummaryResponse>(
      `/creditcards/${creditCardId}/cardmanagement/payments/summary`,
      getRequestConfig(RequestConfig.Default),
    ),
  )

  return requireEchoedCard(summary, creditCardId, 'payment summary')
}

export const fetchCardManagementPayments = async ({
  creditCardId,
  limit,
  before,
}: CardManagementPageInput): Promise<CardManagementPaymentPageResponse> => {
  requireCreditCardId(creditCardId, 'payment reads')

  const page = await getResponseData(
    axios.get<CardManagementPaymentPageResponse>(
      `/creditcards/${creditCardId}/cardmanagement/payments`,
      pageRequestConfig({ limit, before }),
    ),
  )

  return requireEchoedCard(page, creditCardId, 'payment page')
}

export const fetchCardManagementBillingContact = async (
  creditCardId: string,
): Promise<CardManagementBillingContactResponse> => {
  requireCreditCardId(creditCardId, 'billing contact reads')

  return getResponseData(
    axios.get<CardManagementBillingContactResponse>(
      `/creditcards/${creditCardId}/cardmanagement/billing-contact`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const replaceCardManagementBillingContact = async ({
  creditCardId,
  request,
  idempotencyKey,
}: ReplaceCardManagementBillingContactInput): Promise<ReplaceCardManagementBillingContactResponse> => {
  requireCreditCardId(creditCardId, 'billing contact replacements')
  requireIdempotencyKey(idempotencyKey, 'billing contact replacements')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.put<ReplaceCardManagementBillingContactResponse>(
      `/creditcards/${creditCardId}/cardmanagement/billing-contact`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}

export const fetchCardManagementCompanyLine = async (
  creditCardId: string,
): Promise<CardManagementCompanyLineResponse> => {
  requireCreditCardId(creditCardId, 'company line reads')

  return getResponseData(
    axios.get<CardManagementCompanyLineResponse>(
      `/creditcards/${creditCardId}/cardmanagement/company-line`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const fetchCardManagementCurrentCycle = async (
  creditCardId: string,
): Promise<CardManagementCurrentCycleResponse> => {
  requireCreditCardId(creditCardId, 'billing cycle reads')

  const cycle = await getResponseData(
    axios.get<CardManagementCurrentCycleResponse>(
      `/creditcards/${creditCardId}/cardmanagement/billing-cycle/current`,
      getRequestConfig(RequestConfig.Default),
    ),
  )

  return requireEchoedCard(cycle, creditCardId, 'current billing cycle')
}

export const fetchCardManagementPaymentChannels = async (
  creditCardId: string,
): Promise<CardManagementPaymentChannelsResponse> => {
  requireCreditCardId(creditCardId, 'payment channel reads')

  return getResponseData(
    axios.get<CardManagementPaymentChannelsResponse>(
      `/creditcards/${creditCardId}/cardmanagement/payment-channels`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

export const fetchCardManagementGeofence = async (
  creditCardId: string,
): Promise<CardManagementGeofenceResponse> => {
  requireCreditCardId(creditCardId, 'geofence reads')

  return getResponseData(
    axios.get<CardManagementGeofenceResponse>(
      `/creditcards/${creditCardId}/cardmanagement/geofence`,
      getRequestConfig(RequestConfig.Default),
    ),
  )
}

/**
 * Card-scoped country catalogue search.

 *
 * `query` and `before` are omitted rather than sent empty: the contract treats
 * an absent param as "no filter" but a present, blank-or-padded one as
 * `CARD-MGMT.2`. Trimming here means a user who types only spaces gets the
 * unfiltered page instead of a 400.
 */
export const searchCardManagementGeofenceCountries = async (
  {
    creditCardId,
    query,
    limit,
    before,
  }: SearchCardManagementGeofenceCountriesInput,
  signal?: AbortSignal,
): Promise<SearchCardManagementGeofenceCountriesResponse> => {
  requireCreditCardId(creditCardId, 'geofence country searches')

  const trimmedQuery = query?.trim()
  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.get<SearchCardManagementGeofenceCountriesResponse>(
      `/creditcards/${creditCardId}/cardmanagement/geofence/countries`,
      {
        ...requestConfig,
        signal,
        params: {
          ...(trimmedQuery ? { query: trimmedQuery } : {}),
          ...(limit === undefined ? {} : { limit }),
          ...(before ? { before } : {}),
        },
      },
    ),
  )
}

/** One country per call — the contract has no multi-country mutation. */
export const changeCardManagementGeofenceCountry = async ({
  creditCardId,
  request,
  idempotencyKey,
}: ChangeCardManagementGeofenceCountryInput): Promise<ChangeCardManagementGeofenceCountryResponse> => {
  requireCreditCardId(creditCardId, 'geofence country changes')
  requireIdempotencyKey(idempotencyKey, 'geofence country changes')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.post<ChangeCardManagementGeofenceCountryResponse>(
      `/creditcards/${creditCardId}/cardmanagement/geofence/country-changes`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}

/** One channel per call — the contract has no multi-channel mutation. */
export const setCardManagementPaymentChannel = async ({
  creditCardId,
  channel,
  request,
  idempotencyKey,
}: SetPaymentChannelInput): Promise<SetPaymentChannelResponse> => {
  requireCreditCardId(creditCardId, 'payment channel changes')
  requireIdempotencyKey(idempotencyKey, 'payment channel changes')

  const requestConfig = getRequestConfig(RequestConfig.Default)
  return getResponseData(
    axios.put<SetPaymentChannelResponse>(
      `/creditcards/${creditCardId}/cardmanagement/payment-channels/${channel}`,
      request,
      {
        ...requestConfig,
        headers: {
          ...requestConfig.headers,
          'Idempotency-Key': idempotencyKey,
        },
      },
    ),
  )
}
